/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, takeEvery, put, select, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionSuccess,
  SeriesRecording,
  ActionFail,
  XError,
  RecordingsConfigs,
  RecordingsApi,
  SeriesRecordingType,
  Program,
  Recording,
  RecordingsUtils,
  RecordingStatus,
  LEGACY,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import {
  Selectors as SingleRecordingsSelectors,
  ActionCreators as SingleRecordingsActionCreators,
} from './singleRecordings';

export enum ActionType {
  LOAD = 'recordings/series/LOAD',
  UPDATE = 'recordings/series/UPDATE',
  FAIL = 'recordings/series/FAIL',
  RESET = 'recordings/series/RESET',
}

type Request = ActionRequest<ActionType.LOAD>;
type Update = ActionSuccess<ActionType.UPDATE, SeriesRecording[]>;
type Fail = ActionFail<ActionType.FAIL>;
type Reset = ActionSuccess<ActionType.RESET>;

type Actions = Request | Update | Fail | Reset;

const Actions = {
  load: (): Request => ({
    type: ActionType.LOAD,
  }),
  update: (payload: SeriesRecording[]): Update => ({
    type: ActionType.UPDATE,
    payload,
  }),
  fail: (error: XError): Fail => ({
    type: ActionType.FAIL,
    error,
  }),
  reset: (): Reset => ({
    type: ActionType.RESET,
  }),
};

export const ActionCreators = {
  load: (): Request => Actions.load(),
  reset: (): Reset => Actions.reset(),
};

export interface ReducerState {
  data: SeriesRecording[];
  error?: XError;
}

const initialState: ReducerState = {
  data: [],
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.UPDATE: {
      return {
        data: action.payload,
      };
    }
    case ActionType.FAIL:
      return {
        ...state,
        error: action.error,
      };
    case ActionType.RESET:
      return initialState;
    case ActionType.LOAD:
    default:
      return state;
  }
}

// SELECTORS;
export const Selectors = {
  getRecordings: (state: StoreState): SeriesRecording[] => {
    return state.recordings.series.data;
  },
  getError: (state: StoreState): XError | undefined => state.recordings.series.error,
};

function* loadSeriesRecordingsSaga(): SagaIterator {
  try {
    const { npvrVersion }: RecordingsConfigs = yield select(Ducks.Dms.Selectors.getRecordings);
    const seriesRecordings: SeriesRecording[] = yield Ducks.Api.callApi(RecordingsApi.getSeriesRecordings, npvrVersion);

    const storedSeriesRecordings: SeriesRecording[] = yield select(Selectors.getRecordings);
    yield put(Actions.update([...seriesRecordings, ...storedSeriesRecordings]));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

function* genericAddNewSerieRecordingIntoStore(
  epgChannelId: number,
  recordingId: string,
  seriesId: string,
  seasonNumber?: number
): SagaIterator {
  if (!Number.isNaN(recordingId)) {
    const newSerieRecording: SeriesRecording = {
      type: seasonNumber ? SeriesRecordingType.SEASON : SeriesRecordingType.SERIES,
      epgChannelId,
      recordingId: Number(recordingId),
      seriesId,
      seasonNumber,
    };

    const storedSeriesRecordings: SeriesRecording[] = yield select(Selectors.getRecordings);

    if (!seasonNumber) {
      const filteredStoredSeriesRecordings = storedSeriesRecordings.filter(
        (e) =>
          !(
            e.seriesId === newSerieRecording.seriesId &&
            e.epgChannelId === newSerieRecording.epgChannelId &&
            e.type === SeriesRecordingType.SEASON
          )
      );

      yield put(Actions.update([newSerieRecording, ...filteredStoredSeriesRecordings]));
    } else {
      yield put(Actions.update([newSerieRecording, ...storedSeriesRecordings]));
    }
  }
}

function* addByProgramSaga(program: Program): SagaIterator<XError | undefined> {
  try {
    const { npvrVersion }: RecordingsConfigs = yield select(Ducks.Dms.Selectors.getRecordings);

    const { recordingID }: LEGACY.RecordSeriesByProgramIdResponse = yield Ducks.Api.callApi(
      RecordingsApi.addSeriesRecordingByProgramId,
      program.id,
      npvrVersion
    );

    yield call(genericAddNewSerieRecordingIntoStore, program.channelEpgId, recordingID, program.seriesId);
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export function* addBySerieIdSaga(
  seriesId: string,
  channelEpgId: number,
  seasonNumber?: number,
  episodeSeed?: number
): SagaIterator<XError | undefined> {
  try {
    const { recordingID }: LEGACY.RecordSeriesBySeriesIdResponse = yield Ducks.Api.callApi(
      RecordingsApi.addSeriesRecordingBySeriesId,
      seriesId,
      channelEpgId,
      seasonNumber,
      episodeSeed
    );

    yield call(genericAddNewSerieRecordingIntoStore, channelEpgId, recordingID, seriesId, seasonNumber);
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

function* addBySerieIdFromSeasonSaga(seriesId: string, channelEpgId: number, seasonSeed: number): SagaIterator {
  try {
    const { recordingID }: LEGACY.RecordSeriesBySeriesIdResponse = yield Ducks.Api.callApi(
      RecordingsApi.addSeriesRecordingBySeriesIdFromSpecificSeason,
      seriesId,
      channelEpgId,
      seasonSeed
    );

    yield call(genericAddNewSerieRecordingIntoStore, channelEpgId, recordingID, seriesId);
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* cancelSeriesRecordingSaga(seriesRecording: SeriesRecording): SagaIterator<XError | undefined> {
  try {
    const { npvrVersion }: RecordingsConfigs = yield select(Ducks.Dms.Selectors.getRecordings);
    yield Ducks.Api.callApi(RecordingsApi.cancelSeriesRecording, seriesRecording, npvrVersion);

    const storedSeriesRecordings: SeriesRecording[] = yield select(Selectors.getRecordings);
    const filteredSeriesRecordings = storedSeriesRecordings.filter(
      (e) => e.recordingId !== seriesRecording.recordingId
    );

    const storedRecordings: Recording[] = yield select(SingleRecordingsSelectors.getRecordings);
    const filteredStoredRecordings = storedRecordings.filter(
      (e) =>
        !(
          e.seriesId === seriesRecording.seriesId &&
          e.channelEpgId === seriesRecording.epgChannelId &&
          RecordingsUtils.getRecordingStatus(e) === RecordingStatus.SCHEDULED &&
          (!seriesRecording.seasonNumber || seriesRecording.seasonNumber === e.seasonNumber)
        )
    );

    yield put(SingleRecordingsActionCreators.update(filteredStoredRecordings));
    yield put(Actions.update(filteredSeriesRecordings));
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export function* cancelAllSeasonsFromSeriesRecordingSaga(
  seriesRecording: SeriesRecording
): SagaIterator<XError | undefined> {
  try {
    const storedSeriesRecordings: SeriesRecording[] = yield select(Selectors.getRecordings);
    const filteredSeriesRecordings = storedSeriesRecordings.filter(
      (e) =>
        !(
          e.seriesId === seriesRecording.seriesId &&
          e.epgChannelId === seriesRecording.epgChannelId &&
          e.type === SeriesRecordingType.SEASON
        )
    );

    const resps: (XError | undefined)[] = yield all(
      filteredSeriesRecordings.map((e) => call(cancelSeriesRecordingSaga, e))
    );

    const err = resps.find((e) => e !== undefined);
    if (err) {
      throw err;
    }

    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export const SagaFunctions = {
  addBySerieIdSaga,
  cancelSeriesRecordingSaga,
  addBySerieIdFromSeasonSaga,
  cancelAllSeasonsFromSeriesRecordingSaga,
  addByProgramSaga,
};

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionType.LOAD, loadSeriesRecordingsSaga)]);
}
